import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faNoteSticky, faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
import { InboxMessage } from '@spike/omnichannel-model';
import clsx from 'clsx';
import { useAuth } from 'hooks';
import moment from 'moment-timezone';
import React, { useMemo, useState } from 'react';
import ChannelBadge from '../ChannelBadge/ChannelBadge';
import MessageAttachments from './MessageAttachments';
import { splitQuotedReply, stripInlineImagePlaceholders } from '../emailBody';
import styles from './MessageBubble.module.scss';

interface MessageBubbleProps {
    message: InboxMessage;
    clientName: string;
    /** Signed attachment URLs expire; asks the thread to reload when one stops loading. */
    onAttachmentsExpired: () => void;
}

// An email body worth collapsing: longer than what fits without pushing the thread off screen.
const EMAIL_COLLAPSE_LINES = 8;
const EMAIL_COLLAPSE_CHARS = 600;

export const MessageBubble: React.FC<MessageBubbleProps> = ({ message, clientName, onAttachmentsExpired }) => {
    const auth = useAuth();
    const [expanded, setExpanded] = useState(false);
    const [quoteVisible, setQuoteVisible] = useState(false);

    // times belong to the business, not to the browser the staff happens to use
    const time = moment(message.sentAt).tz(auth.timeZone!).format('h:mm a');

    const attachments = message.attachments ?? [];

    const email = useMemo(() => {
        if (message.channel !== 'email') return undefined;

        const { visible, quoted } = splitQuotedReply(stripInlineImagePlaceholders(message.body));
        const lines = visible.split('\n');
        const isLong = lines.length > EMAIL_COLLAPSE_LINES || visible.length > EMAIL_COLLAPSE_CHARS;

        return {
            visible,
            quoted,
            isLong,
            preview: isLong ? lines.slice(0, EMAIL_COLLAPSE_LINES).join('\n') : visible
        };
    }, [message.body, message.channel]);

    if (message.kind === 'notification') {
        return (
            <div className={styles.noteRow}>
                <div className={styles.system}>
                    <span>{message.body}</span>
                    <span className={styles.systemTime}>{time}</span>
                </div>
            </div>
        );
    }

    if (message.kind === 'note') {
        return (
            <div className={styles.noteRow}>
                <div className={styles.note}>
                    <FontAwesomeIcon
                        icon={faNoteSticky}
                        className={styles.noteIcon}
                    />
                    <span>
                        <strong>{message.authorName || 'Staff'}</strong> — {message.body}
                    </span>
                </div>
            </div>
        );
    }

    const isOutbound = message.direction === 'outbound';
    const deliveryLabel = message.failedAt
        ? message.failureReason || 'Failed'
        : message.deliveredAt
        ? 'Delivered'
        : 'Sent';

    if (message.channel === 'email' && email) {
        return (
            <div className={clsx(styles.row, isOutbound && styles.rowOutbound)}>
                <div className={clsx(styles.emailCard, isOutbound && styles.emailCardOutbound)}>
                    {message.subject && <div className={styles.emailSubject}>{message.subject}</div>}
                    <div className={styles.emailFrom}>
                        {isOutbound
                            ? `${message.authorName || 'You'} · from ${message.fromIdentity}`
                            : `${clientName} → ${message.fromIdentity}`}
                    </div>
                    <div className={styles.emailBody}>{expanded ? email.visible : email.preview}</div>

                    {email.isLong && (
                        <button
                            type="button"
                            className={styles.emailToggle}
                            onClick={() => setExpanded(previous => !previous)}
                        >
                            {expanded ? 'Show less' : 'Show more'}
                        </button>
                    )}

                    {email.quoted && (
                        <div className={styles.quoted}>
                            <button
                                type="button"
                                className={styles.quotedToggle}
                                aria-label="Show quoted text"
                                onClick={() => setQuoteVisible(previous => !previous)}
                            >
                                ···
                            </button>
                            {quoteVisible && <div className={styles.quotedBody}>{email.quoted}</div>}
                        </div>
                    )}

                    {attachments.length > 0 && (
                        <MessageAttachments
                            attachments={attachments}
                            onExpired={onAttachmentsExpired}
                        />
                    )}

                    <div className={styles.meta}>
                        <ChannelBadge channel="email" />
                        <span>{time}</span>
                        {isOutbound && (
                            <span className={clsx(styles.delivered, message.failedAt && styles.failed)}>
                                {message.failedAt ? (
                                    <FontAwesomeIcon icon={faTriangleExclamation} />
                                ) : (
                                    <FontAwesomeIcon icon={faCheck} />
                                )}{' '}
                                {deliveryLabel}
                            </span>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={clsx(styles.row, isOutbound && styles.rowOutbound)}>
            <div className={clsx(styles.bubble, isOutbound ? styles.bubbleOutbound : styles.bubbleInbound)}>
                <div className={styles.body}>{message.body}</div>

                {attachments.length > 0 && (
                    <MessageAttachments
                        attachments={attachments}
                        onExpired={onAttachmentsExpired}
                    />
                )}

                <div className={clsx(styles.meta, isOutbound && styles.metaOutbound)}>
                    <ChannelBadge
                        channel="sms"
                        inverted={isOutbound}
                    />
                    <span>
                        {isOutbound
                            ? `${message.authorName ? `${message.authorName} · ` : ''}from ${
                                  message.fromIdentity
                              } · ${time}`
                            : `to ${message.fromIdentity} · ${time}`}
                    </span>
                    {isOutbound && (
                        <span className={clsx(styles.delivered, message.failedAt && styles.failed)}>
                            {message.failedAt ? (
                                <FontAwesomeIcon icon={faTriangleExclamation} />
                            ) : (
                                <FontAwesomeIcon icon={faCheck} />
                            )}{' '}
                            {deliveryLabel}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

export default MessageBubble;
